<template>
	<div ref="sectionContainer" class="space-y-6">
		<!-- Sticky Scale Legend - positioned at top -->
		<ScaleLegend />
		<div class="overflow-hidden border border-gray-200 bg-white/90 backdrop-blur rounded-xl">
			<LikertRow
				v-for="(q, index) in sectionQuestions"
				:key="q.id"
				:ref="(el) => setQuestionRef(el, index)"
				:question="q"
				@answer-changed="handleAnswerChanged(index)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick } from 'vue';
import { questions, type SectionId } from '../data/learningTest';
import { useWizardStore } from '../stores/wizard';
import LikertRow from './LikertRow.vue';
import ScaleLegend from './common/ScaleLegend.vue';

const props = defineProps<{ sectionId: SectionId }>();
const wizardStore = useWizardStore();

// Template refs
const sectionTitle = ref<HTMLElement>();
const sectionContainer = ref<HTMLElement>();
const questionRefs = ref<(HTMLElement | null)[]>([]);

const sectionQuestions = computed(() => questions.filter((q) => q.section === props.sectionId));

// Set question ref for dynamic refs array
function setQuestionRef(el: any, index: number) {
	if (el) {
		questionRefs.value[index] = el.$el || el;
	}
}

// Auto-scroll to next unanswered question after answering (mobile only)
async function handleAnswerChanged(answeredIndex: number) {
	await nextTick();

	// Find the next unanswered question
	const nextUnansweredIndex = sectionQuestions.value.findIndex((q, idx) => {
		return idx > answeredIndex && !wizardStore.answers[q.id];
	});

	// If there's a next unanswered question, apply effects
	if (nextUnansweredIndex !== -1 && questionRefs.value[nextUnansweredIndex]) {
		const targetElement = questionRefs.value[nextUnansweredIndex];

		// Only scroll on mobile - desktop users can see all questions
		const isMobile = window.innerWidth < 640; // sm breakpoint

		if (isMobile) {
			// Calculate scroll position with offset for mobile nav/legend
			const offset = 120; // nav (56px) + legend (~44px) + padding (16px)
			const elementPosition = targetElement.getBoundingClientRect().top;
			const offsetPosition = elementPosition + window.pageYOffset - offset;

			// Smooth scroll to the next question
			window.scrollTo({
				top: offsetPosition,
				behavior: 'smooth',
			});
		}

		// Add temporary highlight effect (both mobile and desktop)
		targetElement.classList.add('question-highlight');
		setTimeout(() => {
			targetElement.classList.remove('question-highlight');
		}, 2000); // Remove highlight after 2 seconds
	}
}

// Focus management for better mobile UX
function focusTitle() {
	if (sectionTitle.value) {
		sectionTitle.value.focus({ preventScroll: true });
	}
}

// Expose methods for parent component
defineExpose({
	focusTitle,
});
</script>

<style scoped>
/* Smooth highlight animation for next question */
@keyframes highlight-pulse {
	0%,
	100% {
		box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
		background-color: transparent;
	}
	50% {
		box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
		background-color: rgba(59, 130, 246, 0.05);
	}
}

.question-highlight {
	animation: highlight-pulse 1s ease-in-out 2;
	border-radius: 0.5rem;
}
</style>
